<template>
  <div class="home-container" :class="{ 'browser-fullscreen': isBrowserFullscreen }" ref="fullscreenContainer">
    <app-navbar
      v-if="!isBrowserFullscreen"
      @close-about="closeAbout"
      @open-about="openAbout"
      :showAbout="showAbout"
    ></app-navbar>
    <main-playing @close-about="closeAbout" :showAbout="showAbout" @open-signup="openSignup"></main-playing>
    <app-footer
      v-if="!isBrowserFullscreen"
      @close-about="closeAbout"
      @open-about="openAbout"
      :showAbout="showAbout"
    ></app-footer>
    
    <!-- Fullscreen button -->
    <button 
      v-if="!isBrowserFullscreen"
      class="fullscreen-button" 
      @click="toggleFullscreen"
      :title="isFullscreen ? 'Exit fullscreen' : 'Enter fullscreen'"
    >
      <svg v-if="!isFullscreen" width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M7 14H5v5h5v-2H7v-3zm-2-4h2V7h3V5H5v5zm12 7h-3v2h5v-5h-2v3zM14 5v2h3v3h2V5h-5z" fill="currentColor"/>
      </svg>
      <svg v-else width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M5 16h3v3h2v-5H5v2zm3-8H5v2h5V5H8v3zm6 11h2v-3h3v-2h-5v5zm2-11V5h-2v5h5V8h-3z" fill="currentColor"/>
      </svg>
    </button>
    
    <!-- Browser fullscreen indicator -->
    <div v-if="isBrowserFullscreen && showFullscreenIndicator" class="browser-fullscreen-indicator">
      <span>Press F11 or ESC to exit fullscreen</span>
    </div>
  </div>
</template>

<script>
import MainPlaying from "../components/main-playing";

export default {
  name: "Playing",
  components: {
    MainPlaying,
  },
  metaInfo: {
    title: "Nuance Radio",
    meta: [
      {
        property: "og:title",
        content: "Nuance Radio",
      },
    ],
  },
  data() {
    return {
      showAbout: false,
      isFullscreen: false,
      isBrowserFullscreen: false,
      showFullscreenIndicator: false,
    };
  },
  mounted() {
    // Listen for fullscreen change events
    document.addEventListener('fullscreenchange', this.handleFullscreenChange);
    document.addEventListener('webkitfullscreenchange', this.handleFullscreenChange);
    document.addEventListener('mozfullscreenchange', this.handleFullscreenChange);
    document.addEventListener('MSFullscreenChange', this.handleFullscreenChange);
    
    // Listen for keyboard events
    document.addEventListener('keydown', this.handleKeydown);
    
    // Check for browser fullscreen mode
    this.checkBrowserFullscreen();
    window.addEventListener('resize', this.checkBrowserFullscreen);
  },
  
  beforeDestroy() {
    // Clean up event listeners
    document.removeEventListener('fullscreenchange', this.handleFullscreenChange);
    document.removeEventListener('webkitfullscreenchange', this.handleFullscreenChange);
    document.removeEventListener('mozfullscreenchange', this.handleFullscreenChange);
    document.removeEventListener('MSFullscreenChange', this.handleFullscreenChange);
    document.removeEventListener('keydown', this.handleKeydown);
    window.removeEventListener('resize', this.checkBrowserFullscreen);
  },
  
  methods: {
    openAbout() {
      this.showAbout = true;
    },
    closeAbout() {
      this.showAbout = false;
    },
    openSignup() {
      this.$emit('open-signup');
    },
    
    async toggleFullscreen() {
      try {
        if (!this.isFullscreen) {
          await this.enterFullscreen();
        } else {
          await this.exitFullscreen();
        }
      } catch (error) {
        console.error('Fullscreen error:', error);
      }
    },
    
    async enterFullscreen() {
      const element = this.$refs.fullscreenContainer;
      
      if (element.requestFullscreen) {
        await element.requestFullscreen();
      } else if (element.webkitRequestFullscreen) {
        await element.webkitRequestFullscreen();
      } else if (element.mozRequestFullScreen) {
        await element.mozRequestFullScreen();
      } else if (element.msRequestFullscreen) {
        await element.msRequestFullscreen();
      }
    },
    
    async exitFullscreen() {
      if (document.exitFullscreen) {
        await document.exitFullscreen();
      } else if (document.webkitExitFullscreen) {
        await document.webkitExitFullscreen();
      } else if (document.mozCancelFullScreen) {
        await document.mozCancelFullScreen();
      } else if (document.msExitFullscreen) {
        await document.msExitFullscreen();
      }
    },
    
    handleFullscreenChange() {
      this.isFullscreen = !!(
        document.fullscreenElement ||
        document.webkitFullscreenElement ||
        document.mozFullScreenElement ||
        document.msFullscreenElement
      );
    },
    
    handleKeydown(event) {
      // Exit fullscreen with ESC key
      if (event.key === 'Escape' && this.isFullscreen) {
        this.exitFullscreen();
      }
    },
    
    checkBrowserFullscreen() {
      // Detect browser fullscreen mode by checking if screen dimensions match window dimensions
      const screenWidth = window.screen.width;
      const screenHeight = window.screen.height;
      const windowWidth = window.innerWidth;
      const windowHeight = window.innerHeight;
      
      const wasBrowserFullscreen = this.isBrowserFullscreen;
      
      // If window size matches screen size, likely in browser fullscreen
      this.isBrowserFullscreen = (
        windowWidth >= screenWidth - 10 && 
        windowHeight >= screenHeight - 10
      );
      
      // Show indicator when entering browser fullscreen
      if (this.isBrowserFullscreen && !wasBrowserFullscreen) {
        this.showFullscreenIndicator = true;
        setTimeout(() => {
          this.showFullscreenIndicator = false;
        }, 3000);
      }
    },
  },
};
</script>

<style scoped>
.home-container {
  width: 100%;
  height: 100%;
  overflow-x: hidden;
  display: flex;
  flex-direction: column;
}

.fullscreen-button {
  position: fixed;
  top: 20px;
  right: 20px;
  width: 48px;
  height: 48px;
  border: none;
  border-radius: 50%;
  background-color: rgba(0, 0, 0, 0.7);
  color: white;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  z-index: 1000;
  backdrop-filter: blur(10px);
}

.fullscreen-button:hover {
  background-color: rgba(0, 0, 0, 0.9);
  transform: scale(1.1);
}

.fullscreen-button:active {
  transform: scale(0.95);
}

/* Hide button on mobile to avoid clutter */
@media (max-width: 768px) {
  .fullscreen-button {
    display: none;
  }
}

/* Fullscreen styles */
.home-container:fullscreen {
  background-color: #000;
}

.home-container:-webkit-full-screen {
  background-color: #000;
}

.home-container:-moz-full-screen {
  background-color: #000;
}

.home-container:-ms-fullscreen {
  background-color: #000;
}

/* Browser fullscreen mode styles */
.home-container.browser-fullscreen {
  background-color: #000;
  padding: 0;
  margin: 0;
}

.home-container.browser-fullscreen .main-playing {
  height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
}

.browser-fullscreen-indicator {
  position: fixed;
  top: 20px;
  left: 50%;
  transform: translateX(-50%);
  background-color: rgba(0, 0, 0, 0.8);
  color: white;
  padding: 10px 20px;
  border-radius: 20px;
  font-size: 14px;
  z-index: 1000;
  backdrop-filter: blur(10px);
  animation: fadeInOut 3s ease-in-out;
}

@keyframes fadeInOut {
  0% { opacity: 0; }
  20% { opacity: 1; }
  80% { opacity: 1; }
  100% { opacity: 0; }
}
</style>
